/**
 * Workspace Actions Hook
 *
 * Provides all action handlers for workspace operations including
 * create, favorite, move, delete, and preview actions.
 *
 * @module features/workspace/hooks/useWorkspaceActions
 */

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { WorkspaceItemType, ConnectorType } from '@loader/shared-types';

import type { PreviewItem } from '../components';
import { useSidePanelStore } from '../stores';
import { useSelectionStore } from '../stores/useSelectionStore';
import { useCreateTableStore } from '../stores/useCreateTableStore';

import { useRpc } from '@/lib/rpc';
import { queryKeys } from '@/lib/queryKeys';
import { toast } from '@/lib/toast';
import { useUndoStore } from '@/store/useUndoStore';
import {
  useToggleFavoriteMutation,
  useTrashItemMutation,
  useBulkTrashMutation,
  useRestoreItemMutation,
  useBulkRestoreMutation,
  usePermanentDeleteMutation,
  useDuplicateLinkedTableMutation,
  DependencyError,
} from '@/features/organization/hooks';
import { useTablesListQuery } from '@/features/tables/hooks/useTableQueries';
import { useLinkedTablesListQuery } from '@/features/linkedTables/hooks/useLinkedTableQueries';

// ============================================================================
// TYPES
// ============================================================================

export interface SelectedItem {
  id: string;
  type: WorkspaceItemType;
}

export interface DialogState {
  createLinkedTable: boolean;
  createFolder: boolean;
  moveToFolder: boolean;
  deleteConfirm: boolean;
  permanentDeleteConfirm: boolean;
  cascadeDeleteConfirm: boolean;
}

export interface DeleteConfirmItem {
  id: string;
  type: WorkspaceItemType;
  name: string;
}

/** Items to be deleted in bulk */
export interface BulkDeleteItems {
  items: Array<{ id: string; type: WorkspaceItemType }>;
  /** Display name for confirmation dialog */
  displayName: string;
}

export interface PreviewState {
  isOpen: boolean;
  item: PreviewItem | null;
}

/** State for cascade delete confirmation */
export interface CascadeDeleteState {
  itemId: string;
  itemType: WorkspaceItemType;
  itemName: string;
  dependentLinkedTables: Array<{ id: string; name: string }>;
}

export interface UseWorkspaceActionsOptions {
  /** Current folder ID (for creating items in this folder) */
  folderId?: string | null;
}

export interface UseWorkspaceActionsResult {
  // Dialog state
  dialogState: DialogState;
  selectedItem: SelectedItem | null;
  /** For bulk move operations */
  selectedItems: SelectedItem[];
  deleteConfirmItem: BulkDeleteItems | null;
  /** Items to be permanently deleted (for confirmation dialog) */
  permanentDeleteConfirmItem: BulkDeleteItems | null;
  previewState: PreviewState;
  /** State for cascade delete confirmation (when deleting table with dependencies) */
  cascadeDeleteState: CascadeDeleteState | null;

  // Loading states
  isCreatingLinkedTable: boolean;

  // Dialog controls
  openCreateLinkedTableDialog: () => void;
  closeCreateLinkedTableDialog: () => void;
  openCreateFolderDialog: () => void;
  closeCreateFolderDialog: () => void;
  openMoveDialog: (id: string, type: WorkspaceItemType) => void;
  closeMoveDialog: () => void;
  closeDeleteConfirmDialog: () => void;
  closePermanentDeleteConfirmDialog: () => void;
  closeCascadeDeleteDialog: () => void;

  // Preview controls
  openPreview: (id: string, type: WorkspaceItemType) => void;
  closePreview: () => void;

  // Action handlers
  // Note: handleCreateTable is no longer here - use useCreateTableStore.openSheet instead
  handleCreateLinkedTable: (data: CreateLinkedTableData) => Promise<void>;
  handleCreateFolder: (data: CreateFolderData) => Promise<void>;
  handleDuplicate: (id: string) => Promise<void>;
  handleToggleFavorite: (id: string, type: WorkspaceItemType) => Promise<void>;
  handleMove: (id: string, type: WorkspaceItemType) => void;
  handleMoveComplete: () => void;
  handleDelete: (id: string, type: WorkspaceItemType, name?: string) => void;
  confirmDelete: () => Promise<void>;
  handleRestore: (id: string, type: WorkspaceItemType) => Promise<void>;
  handleDeletePermanently: (id: string, type: WorkspaceItemType, name?: string) => void;
  confirmPermanentDelete: () => Promise<void>;
  confirmCascadeDelete: () => Promise<void>;

  // Preview action handlers
  handlePreviewToggleFavorite: (id: string, type: WorkspaceItemType) => Promise<void>;
  handlePreviewMove: (id: string, type: WorkspaceItemType) => void;
  handlePreviewDelete: (id: string, type: WorkspaceItemType, name?: string) => void;
}

export interface CreateLinkedTableData {
  name: string;
  description: string;
  tableId: string;
  tableName: string;
}

export interface CreateFolderData {
  name: string;
  parentFolderId?: string | null;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook providing all workspace action handlers
 *
 * Manages dialog state, preview state, and provides handlers for
 * all workspace operations like create, favorite, move, delete.
 */
export function useWorkspaceActions(options: UseWorkspaceActionsOptions = {}): UseWorkspaceActionsResult {
  const { folderId } = options;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const rpc = useRpc();
  const { setAction: setUndoAction } = useUndoStore();

  // Fetch tables and linked tables via TanStack Query for preview functionality
  const { data: tablesData } = useTablesListQuery();
  const { data: linkedTablesData } = useLinkedTablesListQuery();
  const tables = useMemo(() => tablesData ?? [], [tablesData]);
  const linkedTables = useMemo(() => linkedTablesData ?? [], [linkedTablesData]);

  // Mutations
  const toggleFavoriteMutation = useToggleFavoriteMutation();
  const trashItemMutation = useTrashItemMutation();
  const bulkTrashMutation = useBulkTrashMutation();
  const restoreItemMutation = useRestoreItemMutation();
  const bulkRestoreMutation = useBulkRestoreMutation();
  const permanentDeleteMutation = usePermanentDeleteMutation();
  const duplicateLinkedTableMutation = useDuplicateLinkedTableMutation();

  // Selection store - for clearing selection after delete and detecting bulk operations
  const selectedIds = useSelectionStore((state) => state.selectedIds);
  const clearSelection = useSelectionStore((state) => state.clearSelection);

  // Dialog state
  const [dialogState, setDialogState] = useState<DialogState>({
    createLinkedTable: false,
    createFolder: false,
    moveToFolder: false,
    deleteConfirm: false,
    permanentDeleteConfirm: false,
    cascadeDeleteConfirm: false,
  });
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);
  // For bulk move operations
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<BulkDeleteItems | null>(null);
  // For permanent delete confirmation
  const [permanentDeleteConfirmItem, setPermanentDeleteConfirmItem] = useState<BulkDeleteItems | null>(null);
  // For cascade delete confirmation (table with dependent linked tables)
  const [cascadeDeleteState, setCascadeDeleteState] = useState<CascadeDeleteState | null>(null);
  // Loading state for linked table creation
  const [isCreatingLinkedTable, setIsCreatingLinkedTable] = useState(false);

  // Side panel state from global store
  const {
    isOpen: sidePanelIsOpen,
    mode: sidePanelMode,
    previewItem,
    openPreview: openPreviewStore,
    close: closeSidePanelStore,
    togglePreviewFavorite,
  } = useSidePanelStore();

  // Construct preview state for compatibility
  const previewState: PreviewState = {
    isOpen: sidePanelIsOpen && (sidePanelMode === 'previewTable' || sidePanelMode === 'previewLinkedTable'),
    item: previewItem,
  };

  // ============================================================================
  // DIALOG CONTROLS
  // ============================================================================

  const openCreateLinkedTableDialog = useCallback(() => {
    setDialogState((prev) => ({ ...prev, createLinkedTable: true }));
  }, []);

  const closeCreateLinkedTableDialog = useCallback(() => {
    setDialogState((prev) => ({ ...prev, createLinkedTable: false }));
  }, []);

  const openCreateFolderDialog = useCallback(() => {
    setDialogState((prev) => ({ ...prev, createFolder: true }));
  }, []);

  const closeCreateFolderDialog = useCallback(() => {
    setDialogState((prev) => ({ ...prev, createFolder: false }));
  }, []);

  const openMoveDialog = useCallback((id: string, type: WorkspaceItemType) => {
    setSelectedItem({ id, type });
    setDialogState((prev) => ({ ...prev, moveToFolder: true }));
  }, []);

  const closeMoveDialog = useCallback(() => {
    setDialogState((prev) => ({ ...prev, moveToFolder: false }));
    setSelectedItem(null);
    setSelectedItems([]);
  }, []);

  const closeDeleteConfirmDialog = useCallback(() => {
    setDialogState((prev) => ({ ...prev, deleteConfirm: false }));
    setDeleteConfirmItem(null);
  }, []);

  const closePermanentDeleteConfirmDialog = useCallback(() => {
    setDialogState((prev) => ({ ...prev, permanentDeleteConfirm: false }));
    setPermanentDeleteConfirmItem(null);
  }, []);

  const closeCascadeDeleteDialog = useCallback(() => {
    setDialogState((prev) => ({ ...prev, cascadeDeleteConfirm: false }));
    setCascadeDeleteState(null);
  }, []);

  // ============================================================================
  // PREVIEW CONTROLS
  // ============================================================================

  const openPreview = useCallback(
    (id: string, type: WorkspaceItemType) => {
      // Don't open preview for folders
      if (type === 'folder') {
        return;
      }

      const item = findItem(id, type, tables, linkedTables);
      if (item) {
        openPreviewStore(item);
      }
    },
    [tables, linkedTables, openPreviewStore]
  );

  const closePreview = useCallback(() => {
    closeSidePanelStore();
  }, [closeSidePanelStore]);

  // ============================================================================
  // ACTION HANDLERS
  // ============================================================================

  // Note: handleCreateTable is no longer here - table creation now uses
  // the CreateTableSheet component with useCreateTableStore.openSheet

  const handleCreateLinkedTable = useCallback(
    async (data: CreateLinkedTableData) => {
      setIsCreatingLinkedTable(true);
      try {
        const tableSources = [
          {
            id: crypto.randomUUID(),
            type: 'table' as const,
            target_id: data.tableId,
            alias: data.tableName.replace(/[^a-zA-Z0-9_]/g, '_').replace(/^(\d)/, '_$1'),
          },
        ];

        const res = await rpc.api.private['linked-tables'].$post({
          json: {
            name: data.name,
            description: data.description || null,
            folder_id: folderId || null,
            tables: tableSources,
            joins: null,
            filters: null,
            calculated_columns: null,
            row_groups: null,
            values: null,
          },
        });

        if (res.ok) {
          const responseData = await res.json();
          closeCreateLinkedTableDialog();

          // Invalidate TanStack Query cache for linked tables
          // This will refetch the linked tables list in all components that depend on it
          await queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.all });

          // Also invalidate organization items cache for consistency with other mutations
          // This ensures the workspace view updates immediately
          await queryClient.invalidateQueries({ queryKey: queryKeys.organization.items.all });

          void navigate({
            to: folderId ? '/workspace/folder/$id' : '/workspace',
            params: folderId ? { id: folderId } : undefined,
            search: { preview: responseData.linkedTable.id, type: 'linked_table', expanded: true },
          });
        } else {
          const errorData = await res.json() as { error?: string | unknown[]; success?: boolean };
          const errorMessage = Array.isArray(errorData.error)
            ? (errorData.error[0] as { message?: string })?.message ?? 'Validation failed'
            : typeof errorData.error === 'string' ? errorData.error : undefined;
          toast.linkedTable.createdFailed(errorMessage);
        }
      } catch (err) {
        toast.linkedTable.createdFailed(err instanceof Error ? err.message : undefined);
      } finally {
        setIsCreatingLinkedTable(false);
      }
    },
    [rpc, queryClient, navigate, closeCreateLinkedTableDialog, folderId]
  );

  const handleCreateFolder = useCallback(
    async (data: CreateFolderData) => {
      try {
        const res = await rpc.api.private.organization.folders.$post({
          json: {
            name: data.name,
            parent_folder_id: data.parentFolderId || null,
          },
        });

        if (res.ok) {
          closeCreateFolderDialog();
        } else {
          const errorData = await res.json();
          toast.folder.createFailed((errorData as { error?: string }).error);
        }
      } catch (err) {
        toast.folder.createFailed(err instanceof Error ? err.message : undefined);
      }
    },
    [rpc, closeCreateFolderDialog]
  );

  const handleDuplicate = useCallback(
    async (id: string) => {
      try {
        const result = await duplicateLinkedTableMutation.mutateAsync(id);
        const parsed = result as Record<string, unknown> | undefined;
        const linkedTable = parsed?.linkedTable as { id: string; name: string } | undefined;
        toast.workspace.duplicated(linkedTable?.name ?? 'Linked table');
      } catch (err) {
        toast.workspace.duplicateFailed(err instanceof Error ? err.message : undefined);
      }
    },
    [duplicateLinkedTableMutation]
  );

  const handleToggleFavorite = useCallback(
    async (id: string, type: WorkspaceItemType) => {
      try {
        // Mutation's onSuccess invalidates TanStack Query cache automatically
        await toggleFavoriteMutation.mutateAsync({ itemType: type, itemId: id });
      } catch (err) {
        toast.workspace.favoriteFailed(err instanceof Error ? err.message : undefined);
      }
    },
    [toggleFavoriteMutation]
  );

  /**
   * Handle move action - supports bulk move when multiple items are selected
   * If the clicked item is part of a multi-selection, move all selected items
   */
  const handleMove = useCallback(
    (id: string, type: WorkspaceItemType) => {
      // Check if this is a bulk operation:
      // - clicked item is in the selection
      // - multiple items are selected
      const isBulkOperation = selectedIds.has(id) && selectedIds.size > 1;

      if (isBulkOperation) {
        // Gather all selected items with their types
        // Use clicked item's type as fallback for items not found in tables/linkedTables
        const itemsToMove = getSelectedItemsWithTypes(selectedIds, tables, linkedTables, type);
        setSelectedItems(itemsToMove);
        setSelectedItem(null); // Clear single item
      } else {
        // Single item move
        setSelectedItem({ id, type });
        setSelectedItems([]); // Clear bulk items
      }

      setDialogState((prev) => ({ ...prev, moveToFolder: true }));
    },
    [selectedIds, tables, linkedTables]
  );

  /**
   * Handle move completion - just closes dialog
   *
   * Data Flow:
   * 1. MoveToFolderDialog calls useMoveToFolderMutation.mutateAsync()
   * 2. Mutation's onSuccess invalidates TanStack Query cache
   * 3. TanStack Query auto-refetches, updating all subscribed components
   */
  const handleMoveComplete = useCallback(() => {
    closeMoveDialog();
  }, [closeMoveDialog]);

  /**
   * Opens the delete confirmation dialog
   * Supports bulk delete when multiple items are selected
   * Name can be provided directly, or it will be looked up from tables/linkedTables
   */
  const handleDelete = useCallback(
    (id: string, type: WorkspaceItemType, name?: string) => {
      // Check if this is a bulk operation:
      // - clicked item is in the selection
      // - multiple items are selected
      const isBulkOperation = selectedIds.has(id) && selectedIds.size > 1;

      if (isBulkOperation) {
        // Gather all selected items with their types
        const itemsToDelete = getSelectedItemsWithTypes(selectedIds, tables, linkedTables);

        setDeleteConfirmItem({
          items: itemsToDelete,
          displayName: `${itemsToDelete.length} items`,
        });
      } else {
        // Single item delete
        const itemName = name ?? getItemName(id, type, tables, linkedTables);

        setDeleteConfirmItem({
          items: [{ id, type }],
          displayName: `"${itemName}"`,
        });
      }

      setDialogState((prev) => ({ ...prev, deleteConfirm: true }));
    },
    [tables, linkedTables, selectedIds]
  );

  /**
   * Actually performs the delete after user confirms
   * Supports bulk delete when multiple items are selected
   */
  const confirmDelete = useCallback(async () => {
    if (!deleteConfirmItem || deleteConfirmItem.items.length === 0) return;

    const { items } = deleteConfirmItem;
    const isBulkDelete = items.length > 1;

    try {
      // Use bulk mutation for multiple items to avoid race conditions with optimistic updates
      if (isBulkDelete) {
        await bulkTrashMutation.mutateAsync(
          items.map(({ id, type }) => ({ itemId: id, itemType: type }))
        );
      } else {
        // Single item delete
        const { id, type } = items[0];
        await trashItemMutation.mutateAsync({ itemType: type, itemId: id });
      }

      // Clear all deleted items from selection
      clearSelection();

      // Close side panel if we're deleting items currently being viewed or created
      const sidePanelState = useSidePanelStore.getState();
      const createTableStore = useCreateTableStore.getState();

      for (const { id, type } of items) {
        // Check if we're deleting the table currently being created in the wizard
        if (type === 'table' && sidePanelState.mode === 'createTable') {
          if (createTableStore.workingTableId === id) {
            createTableStore.closeSheet();
          }
        }

        // Check if we're deleting the item currently being previewed
        if (
          sidePanelState.previewItem?.id === id &&
          sidePanelState.previewItem?.type === type
        ) {
          sidePanelState.close();
        }
      }

      // Set up undo action for all items
      setUndoAction({
        description: isBulkDelete ? `${items.length} items moved to trash` : 'Item moved to trash',
        undoFn: async () => {
          for (const { id, type } of items) {
            await restoreItemMutation.mutateAsync({ itemType: type, itemId: id });
          }
          // No toast here - the undo action itself is confirmation enough
        },
      });

      // Show toast with undo button (no separate toast on undo - action is confirmation)
      const message = isBulkDelete ? `${items.length} items moved to trash` : 'Moved to trash';
      toast.successWithUndo(message, async () => {
        try {
          for (const { id, type } of items) {
            await restoreItemMutation.mutateAsync({ itemType: type, itemId: id });
          }
          // No toast - the undo action itself is confirmation enough
        } catch (err) {
          toast.workspace.restoreFailed(err instanceof Error ? err.message : undefined);
        }
      });
    } catch (err) {
      toast.workspace.trashFailed(err instanceof Error ? err.message : undefined);
    } finally {
      closeDeleteConfirmDialog();
    }
  }, [deleteConfirmItem, trashItemMutation, bulkTrashMutation, restoreItemMutation, setUndoAction, clearSelection, closeDeleteConfirmDialog]);

  /**
   * Handle restore action - supports bulk restore when multiple items are selected
   * If the clicked item is part of a multi-selection, restore all selected items
   */
  const handleRestore = useCallback(
    async (id: string, type: WorkspaceItemType) => {
      // Check if this is a bulk operation:
      // - clicked item is in the selection
      // - multiple items are selected
      const isBulkOperation = selectedIds.has(id) && selectedIds.size > 1;

      try {
        if (isBulkOperation) {
          // Gather all selected items with their types
          const itemsToRestore = getSelectedItemsWithTypes(selectedIds, tables, linkedTables);

          // Use bulk mutation for batched optimistic updates
          await bulkRestoreMutation.mutateAsync(
            itemsToRestore.map(({ id, type }) => ({ itemId: id, itemType: type }))
          );

          clearSelection();
          toast.workspace.itemRestored();
        } else {
          // Single item restore
          await restoreItemMutation.mutateAsync({ itemType: type, itemId: id });
          toast.workspace.itemRestored();
        }
      } catch (err) {
        toast.workspace.restoreFailed(err instanceof Error ? err.message : undefined);
      }
    },
    [restoreItemMutation, bulkRestoreMutation, selectedIds, tables, linkedTables, clearSelection]
  );

  /**
   * Opens the permanent delete confirmation dialog.
   * Supports bulk permanent delete when multiple items are selected.
   */
  const handleDeletePermanently = useCallback(
    (id: string, type: WorkspaceItemType, name?: string) => {
      // Check if this is a bulk operation
      const isBulkOperation = selectedIds.has(id) && selectedIds.size > 1;

      if (isBulkOperation) {
        // Gather all selected items with their types
        const itemsToDelete = getSelectedItemsWithTypes(selectedIds, tables, linkedTables);

        setPermanentDeleteConfirmItem({
          items: itemsToDelete,
          displayName: `${itemsToDelete.length} items`,
        });
      } else {
        // Single item permanent delete
        const itemName = name ?? getItemName(id, type, tables, linkedTables);

        setPermanentDeleteConfirmItem({
          items: [{ id, type }],
          displayName: `"${itemName}"`,
        });
      }

      setDialogState((prev) => ({ ...prev, permanentDeleteConfirm: true }));
    },
    [selectedIds, tables, linkedTables]
  );

  /**
   * Actually performs the permanent deletion after user confirms.
   * For tables with dependent linked tables, shows cascade confirmation dialog.
   */
  const confirmPermanentDelete = useCallback(async () => {
    if (!permanentDeleteConfirmItem || permanentDeleteConfirmItem.items.length === 0) return;

    const { items } = permanentDeleteConfirmItem;

    try {
      // Delete all items in sequence
      for (const { id, type } of items) {
        await permanentDeleteMutation.mutateAsync({ itemType: type, itemId: id });
      }

      clearSelection();
      toast.workspace.permanentDelete();
    } catch (err) {
      // Check if this is a dependency error (table has linked tables using it)
      if (err instanceof DependencyError) {
        // Get the item that caused the error (last one attempted)
        const lastItem = items[items.length - 1];
        const table = tables.find((t) => t.id === lastItem.id);
        const itemName = table?.name ?? 'this table';

        // Open cascade delete confirmation dialog
        setCascadeDeleteState({
          itemId: lastItem.id,
          itemType: lastItem.type,
          itemName,
          dependentLinkedTables: err.dependentLinkedTables,
        });
        setDialogState((prev) => ({ ...prev, cascadeDeleteConfirm: true }));
      } else {
        toast.workspace.permanentDeleteFailed(err instanceof Error ? err.message : undefined);
      }
    } finally {
      closePermanentDeleteConfirmDialog();
    }
  }, [permanentDeleteConfirmItem, permanentDeleteMutation, clearSelection, tables, closePermanentDeleteConfirmDialog]);

  /**
   * Confirms cascade deletion of a table and its dependent linked tables.
   */
  const confirmCascadeDelete = useCallback(async () => {
    if (!cascadeDeleteState) return;

    const { itemId, itemType } = cascadeDeleteState;

    try {
      // Call permanent delete with cascade: true
      await permanentDeleteMutation.mutateAsync({
        itemType,
        itemId,
        cascade: true,
      });
      clearSelection();
      toast.workspace.permanentDelete();
    } catch (err) {
      toast.workspace.permanentDeleteFailed(err instanceof Error ? err.message : undefined);
    } finally {
      closeCascadeDeleteDialog();
    }
  }, [cascadeDeleteState, permanentDeleteMutation, clearSelection, closeCascadeDeleteDialog]);

  // ============================================================================
  // PREVIEW ACTION HANDLERS
  // ============================================================================

  const handlePreviewToggleFavorite = useCallback(
    async (id: string, type: WorkspaceItemType) => {
      await handleToggleFavorite(id, type);
      togglePreviewFavorite();
    },
    [handleToggleFavorite, togglePreviewFavorite]
  );

  const handlePreviewMove = useCallback(
    (id: string, type: WorkspaceItemType) => {
      closePreview();
      handleMove(id, type);
    },
    [closePreview, handleMove]
  );

  const handlePreviewDelete = useCallback(
    (id: string, type: WorkspaceItemType, name?: string) => {
      closePreview();
      handleDelete(id, type, name);
    },
    [closePreview, handleDelete]
  );

  return {
    // Dialog state
    dialogState,
    selectedItem,
    selectedItems,
    deleteConfirmItem,
    permanentDeleteConfirmItem,
    previewState,
    cascadeDeleteState,

    // Loading states
    isCreatingLinkedTable,

    // Dialog controls
    openCreateLinkedTableDialog,
    closeCreateLinkedTableDialog,
    openCreateFolderDialog,
    closeCreateFolderDialog,
    openMoveDialog,
    closeMoveDialog,
    closeDeleteConfirmDialog,
    closePermanentDeleteConfirmDialog,
    closeCascadeDeleteDialog,

    // Preview controls
    openPreview,
    closePreview,

    // Action handlers
    handleCreateLinkedTable,
    handleCreateFolder,
    handleDuplicate,
    handleToggleFavorite,
    handleMove,
    handleMoveComplete,
    handleDelete,
    confirmDelete,
    handleRestore,
    handleDeletePermanently,
    confirmPermanentDelete,
    confirmCascadeDelete,

    // Preview action handlers
    handlePreviewToggleFavorite,
    handlePreviewMove,
    handlePreviewDelete,
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get selected items with their types by looking up each id in tables and linkedTables.
 * If an item is not found in either, it uses the fallbackType (defaults to 'folder').
 */
function getSelectedItemsWithTypes(
  selectedIds: Set<string>,
  tables: Array<{ id: string }>,
  linkedTables: Array<{ id: string }>,
  fallbackType: WorkspaceItemType = 'folder'
): Array<{ id: string; type: WorkspaceItemType }> {
  const items: Array<{ id: string; type: WorkspaceItemType }> = [];
  selectedIds.forEach((selectedId) => {
    const table = tables.find((t) => t.id === selectedId);
    if (table) {
      items.push({ id: selectedId, type: 'table' });
      return;
    }
    const linkedTable = linkedTables.find((lt) => lt.id === selectedId);
    if (linkedTable) {
      items.push({ id: selectedId, type: 'linked_table' });
      return;
    }
    items.push({ id: selectedId, type: fallbackType });
  });
  return items;
}

/**
 * Get the name of an item by looking it up in tables or linkedTables.
 * Returns 'this item' if not found.
 */
function getItemName(
  id: string,
  type: WorkspaceItemType,
  tables: Array<{ id: string; name: string }>,
  linkedTables: Array<{ id: string; name: string }>
): string {
  if (type === 'table') {
    const table = tables.find((t) => t.id === id);
    return table?.name ?? 'this item';
  } else if (type === 'linked_table') {
    const linkedTable = linkedTables.find((lt) => lt.id === id);
    return linkedTable?.name ?? 'this item';
  }
  return 'this item';
}

/**
 * Find an item in tables or linked tables and convert to PreviewItem
 */
function findItem(
  id: string,
  type: WorkspaceItemType,
  tables: Array<{
    id: string;
    name: string;
    description: string | null;
    is_favorite?: boolean;
    updated_at: string;
    created_at: string;
    connector_type?: ConnectorType | null;
  }>,
  linkedTables: Array<{
    id: string;
    name: string;
    description: string | null;
    is_favorite?: boolean;
    updated_at: string;
    created_at: string;
  }>
): PreviewItem | null {
  if (type === 'table') {
    const table = tables.find((t) => t.id === id);
    if (table) {
      return {
        id: table.id,
        type: 'table',
        name: table.name,
        description: table.description,
        isFavorite: table.is_favorite ?? false,
        updatedAt: table.updated_at,
        createdAt: table.created_at,
        connectorType: table.connector_type,
      };
    }
  } else if (type === 'linked_table') {
    const linkedTable = linkedTables.find((lt) => lt.id === id);
    if (linkedTable) {
      return {
        id: linkedTable.id,
        type: 'linked_table',
        name: linkedTable.name,
        description: linkedTable.description,
        isFavorite: linkedTable.is_favorite ?? false,
        updatedAt: linkedTable.updated_at,
        createdAt: linkedTable.created_at,
      };
    }
  }
  return null;
}
